// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the morning brief as a checklist. "3 things for today": each to-do is
// one line of what to do and one line of why, with its source mark, a tick
// circle and at most one icon action. Loose ends that need nothing sit under
// "Good to know", and the day's facts (meetings, who is out) close the card as
// pills. Ticks are kept on the server (BriefChecklist) so every device agrees,
// and ticking one off tells the engine the brief was used. The server also
// ticks a to-do off by itself once the reply is sent or the task closed.

import styles from '@app/features/konvo_assistant/components/AssistantBriefChecklist.module.css';
import richStyles from '@app/features/konvo_assistant/components/AssistantRichReply.module.css';
import {KonvoCardHead} from '@app/features/konvo_assistant/components/card/KonvoCardHead';
import BriefChecklist from '@app/features/konvo_assistant/state/BriefChecklist';
import {isViewAction, openBriefRef} from '@app/features/konvo_assistant/utils/BriefOpen';
import KonvoConnections from '@app/features/konvo_connections/state/KonvoConnections';
import {ClickUpLogo} from '@app/features/ui/components/icons/ClickUpLogo';
import {KonvoLogo} from '@app/features/ui/components/icons/KonvoLogo';
import {OutlookCalendarLogo} from '@app/features/ui/components/icons/OutlookCalendarLogo';
import {OutlookLogo} from '@app/features/ui/components/icons/OutlookLogo';
import {TeamsLogo} from '@app/features/ui/components/icons/TeamsLogo';
import {WhatsAppLogo} from '@app/features/ui/components/icons/WhatsAppLogo';
import {
	AirplaneTiltIcon,
	ArrowRightIcon,
	BellIcon,
	CalendarBlankIcon,
	CalendarCheckIcon,
	CheckIcon,
	EyeIcon,
	FlagIcon,
	GithubLogoIcon,
	InfoIcon,
	PencilSimpleIcon,
} from '@app/features/ui/icons/Icons';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import type {
	AssistantBriefBlock,
	AssistantBriefFact,
	AssistantBriefItem,
	AssistantBriefSource,
} from '@fluxer/schema/src/domains/konvo/AssistantResponse';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {type ReactNode, useEffect} from 'react';

type ActionIcon = NonNullable<AssistantBriefItem['action']>['icon'];

const RECHECK_MS = 60_000;

function SourceMark({source}: {source: AssistantBriefSource | undefined}) {
	switch (source) {
		case 'mail':
			return <OutlookLogo size={13} />;
		case 'whatsapp':
			return <WhatsAppLogo size={13} />;
		case 'teams':
			return <TeamsLogo size={13} />;
		case 'konvo':
			return <KonvoLogo size={13} />;
		case 'clickup':
			return <ClickUpLogo size={13} />;
		case 'calendar':
			return <OutlookCalendarLogo size={13} />;
		case 'github':
			return <GithubLogoIcon size={13} />;
		default:
			return null;
	}
}

function ActionGlyph({icon}: {icon: ActionIcon}) {
	switch (icon) {
		case 'draft':
			return <PencilSimpleIcon size={15} />;
		case 'view':
			return <EyeIcon size={15} />;
		case 'schedule':
			return <CalendarBlankIcon size={15} />;
		case 'done':
			return <CheckIcon size={15} />;
		default:
			return <ArrowRightIcon size={15} />;
	}
}

function FactGlyph({icon}: {icon: AssistantBriefFact['icon']}) {
	switch (icon) {
		case 'no_meetings':
			return <CalendarCheckIcon size={13} />;
		case 'meetings':
			return <CalendarBlankIcon size={13} />;
		case 'out':
			return <AirplaneTiltIcon size={13} />;
		case 'holiday':
			return <FlagIcon size={13} />;
		case 'reminder':
			return <BellIcon size={13} />;
	}
}

function initials(name: string): string {
	const parts = name.split(/\s+/).filter(Boolean);
	return `${parts[0]?.[0] ?? ''}${parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''}`.toUpperCase();
}

function Detail({item, alertLabel}: {item: AssistantBriefItem; alertLabel: string}) {
	if (!item.detail && !item.source && !item.alert) return null;
	return (
		<span className={styles.detail}>
			{item.source && (
				<span className={styles.source} aria-hidden="true">
					<SourceMark source={item.source} />
				</span>
			)}
			{item.detail}
			{item.alert && <span className={styles.alert} role="img" aria-label={alertLabel} />}
		</span>
	);
}

/**
 * Asks for the ticks when the card appears, then again every minute while it is
 * on screen and when the window comes back: the server ticks off a to-do once
 * the reply is sent or the task closed, wherever that happened.
 */
function useBriefTicks(itemId: string): void {
	useEffect(() => {
		BriefChecklist.ensure(itemId);
		const check = () => {
			if (document.visibilityState === 'visible') BriefChecklist.ensure(itemId);
		};
		const timer = window.setInterval(check, RECHECK_MS);
		window.addEventListener('focus', check);
		return () => {
			window.clearInterval(timer);
			window.removeEventListener('focus', check);
		};
	}, [itemId]);
}

export const AssistantBriefChecklist = observer(
	({
		block,
		kindIcon,
		label,
		onAction,
		disabled,
	}: {
		block: AssistantBriefBlock;
		kindIcon: ReactNode;
		label: string;
		onAction?: (prompt: string) => void;
		disabled: boolean;
	}) => {
		const {t} = useLingui();
		const itemId = block.item_id;
		useBriefTicks(itemId);
		const todos = block.todos ?? [];
		const notes = block.notes ?? [];
		const facts = block.facts ?? [];
		const done = BriefChecklist.get(itemId);
		const remaining = todos.filter((_, index) => !done.has(index)).length;
		const heading =
			remaining === 0
				? t`All done for today`
				: remaining === todos.length
					? remaining === 1
						? t`1 thing for today`
						: t`${remaining} things for today`
					: remaining === 1
						? t`1 thing left today`
						: t`${remaining} things left today`;
		// The first open to-do with an action gets the filled button; the rest stay quiet.
		const primary = todos.findIndex((todo, index) => todo.action && !done.has(index));
		const alertLabel = t`Something is broken`;

		return (
			<section
				className={clsx(richStyles.suggestion, styles.checklist)}
				data-kind={block.kind}
				data-flx="konvo-assistant.structured-response.brief.checklist"
			>
				<KonvoCardHead
					className={richStyles.suggestionHeadShared}
					icon={kindIcon}
					label={label}
					end={block.when ? <span className={styles.when}>{block.when}</span> : null}
				/>
				<strong className={styles.heading}>{heading}</strong>
				{block.body && <p className={styles.summary}>{block.body}</p>}

				<ul className={styles.todos}>
					{todos.map((todo, index) => {
						const ticked = done.has(index);
						const action = todo.action;
						return (
							<li
								// The list is fixed once delivered, so the position is the identity the server keeps too.
								key={index}
								className={clsx(styles.todo, ticked && styles.ticked)}
								data-flx="konvo-assistant.structured-response.brief.todo"
							>
								<button
									type="button"
									role="checkbox"
									aria-checked={ticked}
									aria-label={todo.title}
									className={styles.tick}
									onClick={() => BriefChecklist.toggle(itemId, index)}
									data-flx="konvo-assistant.structured-response.brief.tick"
								>
									<CheckIcon size={11} weight="bold" />
								</button>
								<span className={styles.todoText}>
									<span className={styles.todoTitle}>{todo.title}</span>
									<Detail item={todo} alertLabel={alertLabel} />
								</span>
								{action && onAction && !ticked && (
									<Tooltip text={action.label} position="top">
										<button
											type="button"
											className={clsx(styles.act, index === primary && styles.actPrimary)}
											aria-label={action.label}
											disabled={disabled}
											onClick={(event) => {
												KonvoConnections.reportProactiveOutcome(itemId, 'used');
												// A "show me" on a to-do that knows its thread opens it in the peek.
												if (todo.ref && isViewAction(action)) {
													const row = event.currentTarget.closest<HTMLElement>('li');
													openBriefRef(todo.ref, todo.title, row, () => onAction(action.prompt));
													return;
												}
												onAction(action.prompt);
											}}
											data-flx="konvo-assistant.structured-response.brief.todo-action"
										>
											<ActionGlyph icon={action.icon} />
										</button>
									</Tooltip>
								)}
							</li>
						);
					})}
				</ul>

				{notes.length > 0 && (
					<>
						<div className={styles.sectionLabel}>{t`Good to know`}</div>
						<ul className={styles.notes}>
							{notes.map((note) => (
								<li key={note.title} className={styles.note}>
									<span className={styles.noteMark} aria-hidden="true">
										{note.source && note.source !== 'other' ? (
											<SourceMark source={note.source} />
										) : (
											<InfoIcon size={14} />
										)}
									</span>
									<span>
										{note.title}
										{note.detail && <span className={styles.noteDetail}> · {note.detail}</span>}
									</span>
								</li>
							))}
						</ul>
					</>
				)}

				{facts.length > 0 && (
					<div className={styles.facts}>
						{facts.map((fact) => (
							<span key={fact.text} className={styles.fact} title={fact.people?.join(', ')}>
								{fact.people?.length ? (
									<span className={styles.faces} aria-hidden="true">
										{fact.people.slice(0, 4).map((name) => (
											<span key={name} className={styles.face}>
												{initials(name)}
											</span>
										))}
									</span>
								) : (
									<span className={styles.factIcon} aria-hidden="true">
										<FactGlyph icon={fact.icon} />
									</span>
								)}
								{fact.text}
							</span>
						))}
					</div>
				)}
			</section>
		);
	},
);
